/* global gdAlertify */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../config/axiosConfig";
import { formatMoney } from "../../utils/utils";
import { BadgeEstado, mensajeError } from "./enviosUi";
import {
    MESES, TIPOS_HONORARIOS, cargarFilasHonorarios, clavesDe, datosHonorarios, nombreDe, normalizarTelefono,
    urlImagenHonorarios, valoresParaClaves, variablesParaEnvio,
} from "./honorariosWsp";

const componente = (plantilla, tipo) => (plantilla?.componentes || []).find((c) => String(c.type).toUpperCase() === tipo);

/**
 * Crea el envío de WhatsApp de honorarios de un período ya liquidado y confirmado.
 * Encola un mensaje por auxiliar en un solo paso; la imagen de su "Resumen de Pago" la genera
 * el backend al procesar la cola. Nada sale hasta "Procesar cola ahora". En modo demo, 1 mensaje.
 *
 * tipo: "adelanto" | "fm"
 */
const NotificarHonorariosModal = ({ visible, onClose, periodo, tipo }) => {
    const navigate = useNavigate();
    const [info, setInfo] = useState(null);
    const [filas, setFilas] = useState([]);
    const [plantilla, setPlantilla] = useState(null);
    const [cargando, setCargando] = useState(false);
    const [errorCarga, setErrorCarga] = useState(null);
    const [seleccion, setSeleccion] = useState(new Set());
    const [demoId, setDemoId] = useState(null);
    const [fase, setFase] = useState("listo"); // listo | creando | fin
    const [resultados, setResultados] = useState([]);
    const [loteId, setLoteId] = useState(null);
    const [vista, setVista] = useState(null); // { nombre, url } vista previa de la imagen

    const def = TIPOS_HONORARIOS[tipo];
    const [anio, mes] = (periodo || "").split("-").map(Number);
    const periodoTexto = `${MESES[mes - 1] || ""} ${anio || ""}`;

    useEffect(() => {
        if (!visible || !periodo) return;
        setFase("listo");
        setResultados([]);
        setErrorCarga(null);
        (async () => {
            setCargando(true);
            try {
                const { data } = await axiosInstance.get("/envios/honorarios/estado", { params: { periodo, tipo } });
                const estado = data?.data;
                setInfo(estado);
                const [lista, plantillas] = await Promise.all([
                    cargarFilasHonorarios(periodo, tipo),
                    estado.canal.configurado
                        ? axiosInstance.get(`/envios/canales/${estado.canal.id}/plantillas`).then((r) => r.data?.data || []).catch(() => [])
                        : Promise.resolve([]),
                ]);
                setFilas(lista);
                setPlantilla(plantillas.find((p) => p.nombre === estado.plantilla && p.idioma === estado.plantilla_idioma) || null);
            } catch (error) {
                setErrorCarga(mensajeError(error, "No se pudo preparar la notificación."));
            } finally {
                setCargando(false);
            }
        })();
    }, [visible, periodo, tipo]);

    useEffect(() => () => { if (vista?.url) URL.revokeObjectURL(vista.url); }, [vista]);

    // Cada auxiliar con su total, teléfono y motivo de exclusión (si aplica).
    const candidatos = useMemo(() => filas.map((row) => {
        const id = Number(row.auxiliar_id);
        const datos = datosHonorarios(row, tipo, periodo);
        const telefono = normalizarTelefono(row.fono_auxiliar);
        const previo = info?.notificados?.[id] || null;
        let motivo = null;
        // Solo se notifica a quien está en la foto de la liquidación confirmada (el monto liquidado).
        if (info && !(info.liquidados || []).includes(id)) motivo = "No está en la liquidación";
        else if (datos.total <= 0) motivo = "Total $0";
        else if (!telefono) motivo = "Sin teléfono válido";
        // Los envíos de modo demo (al número de pruebas) no cuentan como notificada.
        else if (previo && !previo.demo && previo.estado !== "fallido") motivo = "Ya notificada";
        return { id, row, nombre: nombreDe(row), datos, telefono, previo, motivo };
    }).sort((a, b) => a.nombre.localeCompare(b.nombre)), [filas, info, tipo, periodo]);

    const elegibles = useMemo(() => candidatos.filter((c) => !c.motivo), [candidatos]);

    useEffect(() => {
        setSeleccion(new Set(elegibles.map((c) => c.id)));
        setDemoId(elegibles[0]?.id ?? null);
    }, [elegibles]);

    const claves = clavesDe(componente(plantilla, "BODY")?.text);
    const aEnviar = info?.modo_demo
        ? elegibles.filter((c) => c.id === demoId).slice(0, 1)
        : elegibles.filter((c) => seleccion.has(c.id));

    // Lo que impide crear el envío.
    const bloqueo = !info ? null
        : !info.liquidacion_confirmada ? `La liquidación de ${def.corto} de ${periodoTexto} no está confirmada.`
        : !info.canal.configurado ? "El canal de WhatsApp no está configurado (Motor de Envíos > Canales)."
        : !plantilla ? `La plantilla "${info.plantilla}" (${info.plantilla_idioma}) no está en YCloud. Revisa el nombre en Motor de Envíos > Parámetros.`
        : plantilla.estado !== "APPROVED" ? `La plantilla "${info.plantilla}" no está aprobada (${plantilla.estado}).`
        : String(componente(plantilla, "HEADER")?.format || "").toUpperCase() !== "IMAGE" ? `La plantilla "${info.plantilla}" no tiene encabezado de imagen.`
        : null;

    const alternar = (id) => setSeleccion((prev) => {
        const nuevo = new Set(prev);
        nuevo.has(id) ? nuevo.delete(id) : nuevo.add(id);
        return nuevo;
    });

    const verImagen = async (c) => {
        setVista({ nombre: c.nombre, url: null });
        try {
            setVista({ nombre: c.nombre, url: await urlImagenHonorarios(periodo, tipo, c.id) });
        } catch (error) {
            setVista(null);
            gdAlertify.notiError(error.message);
        }
    };

    const crearEnvio = async () => {
        if (bloqueo || !aEnviar.length) return;
        setFase("creando");
        try {
            const { data } = await axiosInstance.post("/envios/honorarios/lote", { periodo, tipo });
            const id = data?.data?.id;
            setLoteId(id);
            const items = aEnviar.map((c) => ({
                auxiliar_id: c.id,
                nombre: c.nombre,
                telefono: c.telefono,
                variables: variablesParaEnvio(componente(plantilla, "BODY")?.text, claves, valoresParaClaves(claves, c.datos.variables)),
            }));
            const resp = await axiosInstance.post(`/envios/honorarios/lote/${id}/mensajes`, { items: JSON.stringify(items) });
            const porId = Object.fromEntries(aEnviar.map((c) => [c.id, c]));
            setResultados((resp.data?.data || []).map((r) => ({ ...r, c: porId[r.auxiliar_id] })));
            setFase("fin");
        } catch (error) {
            gdAlertify.notiError(mensajeError(error, "No se pudo crear el envío."));
            setFase("listo");
        }
    };

    if (!visible) return null;

    // Mismo botón arriba de la tabla y en el pie del modal.
    const textoCrear = info?.modo_demo
        ? "Crear envío de prueba (1 mensaje)"
        : aEnviar.length === elegibles.length
            ? `Notificar a todas (${aEnviar.length})`
            : `Notificar seleccionadas (${aEnviar.length})`;
    const botonCrear = (
        <button className="btn btn-success" onClick={crearEnvio} disabled={!!bloqueo || !aEnviar.length || cargando || fase === "creando"}>
            {fase === "creando"
                ? <span className="spinner-border spinner-border-sm me-1"></span>
                : <i className="bi bi-whatsapp me-1"></i>}
            {textoCrear}
        </button>
    );

    const creados = resultados.filter((r) => r.ok).length;
    const omitidos = resultados.filter((r) => r.omitido).length;
    // Auxiliares que ya tenían un mensaje fallido: se reenvía ese mismo (intento siguiente), no uno nuevo.
    const reenviados = resultados.filter((r) => r.ok && r.reenviado).length;
    const conError = resultados.filter((r) => !r.ok && !r.omitido);

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)", overflowY: "auto" }}>
            {/* scrollable: el encabezado y el pie (con el botón) quedan siempre visibles; solo scrollea el cuerpo */}
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header bg-dark text-white">
                        <h5 className="modal-title">
                            <i className="bi bi-whatsapp me-2"></i>
                            Notificar honorarios · {def.corto} · {periodoTexto}
                        </h5>
                        <button type="button" className="btn-close btn-close-white" onClick={onClose} disabled={fase === "creando"}></button>
                    </div>

                    <div className="modal-body">
                        {cargando && (
                            <div className="text-center py-4"><div className="spinner-border text-primary" role="status"></div></div>
                        )}
                        {errorCarga && <div className="alert alert-danger">{errorCarga}</div>}

                        {!cargando && info && (
                            <>
                                {bloqueo && <div className="alert alert-danger"><i className="bi bi-x-octagon me-2"></i>{bloqueo}</div>}

                                {info.modo_demo ? (
                                    <div className="alert alert-warning">
                                        <i className="bi bi-cone-striped me-2"></i>
                                        <strong>Modo DEMO:</strong> se crea <strong>1 mensaje</strong> y al procesarlo sale al número demo{" "}
                                        <strong>{info.demo_telefono || "(sin configurar: configúralo en Parámetros)"}</strong>, no a la auxiliar.
                                        <div className="mt-2 d-flex align-items-center gap-2">
                                            <span className="small">Auxiliar de la prueba:</span>
                                            <select className="form-select form-select-sm w-auto" value={demoId ?? ""} disabled={fase !== "listo"}
                                                onChange={(e) => setDemoId(Number(e.target.value))}>
                                                {elegibles.map((c) => <option key={c.id} value={c.id}>{c.nombre} · ${formatMoney(c.datos.total)}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="alert alert-success">
                                        <i className="bi bi-broadcast me-2"></i>
                                        <strong>Modo PRODUCTIVO:</strong> se crea un mensaje por cada auxiliar seleccionada y al procesarlo le llega a ella.
                                    </div>
                                )}

                                <div className="small text-muted mb-3">
                                    Plantilla <code>{info.plantilla}</code> ({info.plantilla_idioma}). La imagen del <strong>Resumen de Pago</strong> de
                                    cada auxiliar se genera en el servidor al enviar (copia privada en S3).
                                    {info.lote && <> Se agrega al envío <strong>#{info.lote.id}</strong> ya existente de este período.</>}
                                    {" "}Nada sale hasta <strong>Motor de Envíos → Procesar cola ahora</strong>.
                                </div>

                                {fase === "listo" && (
                                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                                        <small className="text-muted">
                                            <strong>{aEnviar.length}</strong> por notificar · {candidatos.length - elegibles.length} excluida(s) · {candidatos.length} en total
                                        </small>
                                        {botonCrear}
                                    </div>
                                )}

                                {fase !== "fin" && (
                                    // Contenedor propio con scroll (vertical y horizontal): la tabla no se sale del modal.
                                    <div className="border rounded" style={{ maxHeight: "45vh", overflow: "auto" }}>
                                        <table className="table table-sm table-hover align-middle mb-0">
                                            <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                                <tr>
                                                    {!info.modo_demo && (
                                                        <th style={{ width: 32 }}>
                                                            <input type="checkbox" className="form-check-input" disabled={fase !== "listo"}
                                                                checked={elegibles.length > 0 && seleccion.size === elegibles.length}
                                                                onChange={(e) => setSeleccion(new Set(e.target.checked ? elegibles.map((c) => c.id) : []))} />
                                                        </th>
                                                    )}
                                                    <th>Auxiliar</th>
                                                    <th>Teléfono</th>
                                                    <th className="text-end">Total a pagar</th>
                                                    <th>Estado</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {candidatos.map((c) => (
                                                    <tr key={c.id} className={c.motivo ? "text-muted" : ""}>
                                                        {!info.modo_demo && (
                                                            <td>
                                                                <input type="checkbox" className="form-check-input" disabled={!!c.motivo || fase !== "listo"}
                                                                    checked={!c.motivo && seleccion.has(c.id)} onChange={() => alternar(c.id)} />
                                                            </td>
                                                        )}
                                                        <td>{c.nombre}</td>
                                                        <td><small>{c.telefono || c.row.fono_auxiliar || "—"}</small></td>
                                                        <td className="text-end">${formatMoney(c.datos.total)}</td>
                                                        <td>
                                                            {c.motivo === "Ya notificada" ? <BadgeEstado estado={c.previo.estado} />
                                                                : c.motivo ? <span className="badge bg-secondary">{c.motivo}</span>
                                                                : c.previo?.estado === "fallido" ? <span className="badge bg-warning text-dark">Falló antes: se reintenta</span>
                                                                : c.previo?.demo ? <span className="badge bg-light text-dark border">Por notificar <span className="text-muted">(probada en demo)</span></span>
                                                                : <span className="badge bg-light text-dark border">Por notificar</span>}
                                                        </td>
                                                        <td className="text-end">
                                                            {c.datos.total > 0 && (
                                                                <button className="btn btn-sm btn-link p-0" onClick={() => verImagen(c)} title="Ver la imagen que se enviará">
                                                                    <i className="bi bi-image"></i>
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                                {!candidatos.length && (
                                                    <tr><td colSpan="6" className="text-center text-muted py-3">No hay honorarios en este período.</td></tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {fase === "fin" && (
                                    <div className="alert alert-light border">
                                        <div>
                                            <i className="bi bi-check-circle text-success me-1"></i>
                                            <strong>{creados}</strong> mensaje(s) en la cola del envío <strong>#{loteId}</strong> (pendientes).
                                        </div>
                                        {reenviados > 0 && <div><i className="bi bi-arrow-repeat me-1"></i>{reenviados} de ellos son reenvíos de mensajes fallidos (mismo mensaje, intento siguiente).</div>}
                                        {omitidos > 0 && <div><i className="bi bi-skip-forward me-1"></i>{omitidos} omitido(s): ya estaban notificados.</div>}
                                        {conError.length > 0 && (
                                            <div className="mt-2">
                                                <i className="bi bi-x-circle text-danger me-1"></i>{conError.length} con error:
                                                <ul className="mb-0 small">
                                                    {conError.map((r) => <li key={r.auxiliar_id}>{r.c?.nombre || r.auxiliar_id}: {r.error}</li>)}
                                                </ul>
                                            </div>
                                        )}
                                        <div className="mt-2 small">Para enviarlos: <strong>Motor de Envíos → Procesar cola ahora</strong>.</div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose} disabled={fase === "creando"}>Cerrar</button>
                        {fase === "fin" && (
                            <button className="btn btn-primary" onClick={() => navigate("/motor-envios")}>
                                <i className="bi bi-send me-1"></i>Ir al Motor de Envíos
                            </button>
                        )}
                        {fase !== "fin" && botonCrear}
                    </div>
                </div>
            </div>

            {vista && (
                <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)", overflowY: "auto", zIndex: 1060 }}
                    onClick={() => setVista(null)}>
                    <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h6 className="modal-title">Imagen de {vista.nombre}</h6>
                                <button type="button" className="btn-close" onClick={() => setVista(null)}></button>
                            </div>
                            <div className="modal-body text-center">
                                {vista.url
                                    ? <img src={vista.url} alt={`Detalle de ${vista.nombre}`} className="img-fluid border" />
                                    : <div className="py-5"><span className="spinner-border text-primary"></span></div>}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificarHonorariosModal;
